import { FunctionComponent, MutableRefObject } from "react";
import { useTranslation } from "react-i18next";

import { inviteStopsForAlreadyConnected } from "@signalist/shared-types";

import { Information } from "@/components/Information/Information";
import { Toggle } from "@/components/Toggle/Toggle";
import { classNames } from "@/utils/classNames";

import { MyNode } from "../../CampaignOverview";
import { EmailNodeConfig } from "../nodes";

interface InviteAlreadyConnectedToggleProps {
	selectedNode: { id: string; data: object } | null;
	setSelectedNode: (node: EmailNodeConfig) => void;
	nodesRef: MutableRefObject<MyNode[]>;
	setIsDirty?: (isDirty: boolean) => void;
	onUpdate?: () => void;
}

/**
 * "Stop the campaign for leads who are already connected" on a "Send LinkedIn Invite" step
 * (PREC-4426), stored as `stopIfAlreadyConnected` on the invite node in `list.campaignConfig`. On:
 * a lead the sender is already connected to gets no invite, the campaign ends for that lead and
 * the lead shows in Outbox → Error. Off (and every step stored before the switch existed): the
 * invite is skipped and the next step runs. Read at send time by the API's `runCampaignStep`.
 */
export const InviteAlreadyConnectedToggle: FunctionComponent<InviteAlreadyConnectedToggleProps> = ({
	selectedNode,
	setSelectedNode,
	nodesRef,
	setIsDirty,
	onUpdate,
}) => {
	const { t } = useTranslation();
	const isOn = inviteStopsForAlreadyConnected(selectedNode?.data);

	const toggle = () => {
		if (!selectedNode) {
			return;
		}

		const data = { ...selectedNode.data, stopIfAlreadyConnected: !isOn };

		setSelectedNode({ ...selectedNode, data } as unknown as EmailNodeConfig);
		nodesRef.current = nodesRef.current.map((node) =>
			node.id === selectedNode.id
				? { ...node, data: { ...node.data, stopIfAlreadyConnected: !isOn } }
				: node
		);
		setIsDirty?.(true);
		onUpdate?.();
	};

	return (
		<div
			className={classNames(
				"text-sm flex flex-col gap-1 items-start mr-4 mt-4 mb-1 rounded-lg border p-3",
				isOn ? "border-blue-action-200 bg-blue-action-200/40" : "border-border-light"
			)}
		>
			<div className="flex flex-row items-start gap-2">
				<Toggle
					className="mt-0.5"
					isChecked={isOn}
					testId="invite-stop-already-connected"
					onChange={toggle}
				/>
				<div className="flex flex-col gap-0.5">
					<div className="flex flex-row items-center gap-1">
						{t("list.campaignBuilder.inviteStopLabel")}
						{isOn && <Information information={t("list.campaignBuilder.inviteStopTooltip")} />}
					</div>
					<div className="text-xs text-text-meta whitespace-normal">
						{t(
							isOn
								? "list.campaignBuilder.inviteStopOnDescription"
								: "list.campaignBuilder.inviteStopOffDescription"
						)}
					</div>
				</div>
			</div>
		</div>
	);
};
